import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Target, Users, Flag, Wrench, Building2, X, Plus } from "lucide-react";

export const DIMS = [
  { key: "expectations", label: "Expectations vs. actual tasks", icon: Target },
  { key: "stakeholders", label: "Stakeholders and decisions", icon: Users },
  { key: "success", label: "Success criteria, 90 days / 12 months", icon: Flag },
  { key: "tools", label: "Tools, data and research maturity", icon: Wrench },
  { key: "team", label: "Team, reporting line, culture", icon: Building2 },
];

function DimCard({ dim, bullets, onChange }) {
  const [draft, setDraft] = useState("");
  const Icon = dim.icon;
  const filled = bullets.length > 0;
  const add = () => {
    if (!draft.trim()) return;
    onChange([...bullets, draft.trim()]);
    setDraft("");
  };
  return (
    <motion.section
      layout
      data-testid={`profile-dim-${dim.key}`}
      className={`p-5 border rounded-lg transition-colors ${filled ? "bg-white border-[#14352a]" : "bg-[#faf8f5] border-dashed border-[#d4cebe]"}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-8 w-8 grid place-items-center rounded-md ${filled ? "bg-[#14352a] text-[#d9a92e]" : "bg-[#f2ece4] text-[#86928a]"}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="font-display text-base text-[#14352a]">{dim.label}</h3>
        <span className="ml-auto font-mono-label text-[9px] text-[#86928a]">{bullets.length}</span>
      </div>
      <ul className="mt-3 space-y-1.5 min-h-[28px]">
        <AnimatePresence initial={false}>
          {bullets.map((b, i) => (
            <motion.li
              key={b + i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="group flex gap-2 text-sm text-[#14352a] leading-snug"
            >
              <span className="text-[#9a7218] mt-0.5">◆</span>
              <span className="flex-1">{b}</span>
              <button
                data-testid={`profile-remove-${dim.key}-${i}`}
                onClick={() => onChange(bullets.filter((_, j) => j !== i))}
                className="opacity-0 group-hover:opacity-100 text-[#86928a] hover:text-[#b3261e]"
                aria-label="Remove"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.li>
          ))}
        </AnimatePresence>
        {!filled && <li className="text-xs text-[#86928a] italic">Nothing learned yet. Ask about it.</li>}
      </ul>
      <div className="mt-3 flex gap-2 no-print">
        <input
          data-testid={`profile-add-input-${dim.key}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a note by hand"
          className="flex-1 h-8 px-2.5 text-xs border border-[#e5e0d8] rounded-md bg-white focus:outline-none focus:border-[#9a7218]"
        />
        <button data-testid={`profile-add-btn-${dim.key}`} onClick={add} className="h-8 w-8 grid place-items-center rounded-md border border-[#e5e0d8] hover:border-[#9a7218]" aria-label="Add">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.section>
  );
}

export default function RoleProfile({ profile, onChange }) {
  const total = DIMS.reduce((a, d) => a + (profile[d.key]?.length || 0), 0);
  const covered = DIMS.filter((d) => profile[d.key]?.length).length;
  return (
    <div data-testid="role-profile">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono-label text-[10px] text-[#9a7218]">Live role profile</div>
          <div className="mt-1 text-sm text-[#55625b]">{covered} / 5 dimensions covered · {total} facts</div>
        </div>
        <div className="flex gap-1">
          {DIMS.map((d) => (
            <span key={d.key} data-testid={`profile-dot-${d.key}`} className={`h-2 w-6 rounded-full ${profile[d.key]?.length ? "bg-[#9a7218]" : "bg-[#e5e0d8]"}`} />
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-3">
        {DIMS.map((d) => (
          <DimCard key={d.key} dim={d} bullets={profile[d.key] || []} onChange={(b) => onChange({ ...profile, [d.key]: b })} />
        ))}
      </div>
    </div>
  );
}
